import { useQueryClient } from '@tanstack/react-query';
import { FileSignature, ImagePlus, Loader2, Minus, PenTool, Save, ShieldCheck, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import {
  useMeControllerDefinirAssinatura,
  useMeControllerEnviarAssinaturaImagem,
  useMeControllerRemoverAssinatura,
} from '@/api/generated/me/me';
import {
  DefinirAssinaturaDtoFonte,
  MeAssinaturaTipo,
  type MeResponse,
} from '@/api/generated/model';
import { AssinaturaDesenhada } from '@/features/perfil/components/AssinaturaDesenhada';
import { CertificadoDigital } from '@/features/perfil/components/CertificadoDigital';
import { aoResponder } from '@/features/perfil/lib/aoResponder';
import { PreviaDaAssinatura } from '@/features/perfil/components/PreviaDaAssinatura';
import { CampoDeImagem } from '@/shared/components/CampoDeImagem';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { cn } from '@/shared/lib/cn';

/** As cinco cursivas do contrato — as mesmas que o PDF embute. */
type Fonte = (typeof DefinirAssinaturaDtoFonte)[keyof typeof DefinirAssinaturaDtoFonte];

const FONTES = Object.values(DefinirAssinaturaDtoFonte);

/**
 * As quatro formas de assinar, e por que cada uma tem um caminho diferente:
 *
 * - **Nenhuma** e **digitada** não têm arquivo → `PUT /v1/me/assinatura` (JSON).
 * - **Rubrica** e **imagem** viram PNG → `POST /v1/me/assinatura/imagem`.
 * - **Certificado** → o que ASSINA o PDF: arquivo A1 (`.pfx`) ou SafeID em
 *   nuvem, cada um com rotas próprias — ver `CertificadoDigital`.
 *
 * A aba aberta é derivada do tipo salvo, não de estado local com efeito: sem
 * isso, salvar num tipo e o servidor devolver outro deixaria a tela mentindo.
 */
export function AssinaturaCard({ me }: { me: MeResponse }) {
  const queryClient = useQueryClient();
  const { assinatura } = me.perfil;

  const definir = useMeControllerDefinirAssinatura();
  const enviarImagem = useMeControllerEnviarAssinaturaImagem();
  const remover = useMeControllerRemoverAssinatura();

  const [texto, setTexto] = useState(assinatura.texto ?? me.perfil.nomeCompleto);
  // O servidor devolve `fonte` como string livre; o select só aceita uma das
  // cinco do contrato. Fonte desconhecida (vinda de um dado antigo) cai na
  // mesma padrão que o `EmissorService` usa.
  const [fonte, setFonte] = useState<Fonte>(fonteConhecida(assinatura.fonte));
  const [aba, setAba] = useState(abaInicial(assinatura.tipo));

  const ocupado = definir.isPending || enviarImagem.isPending || remover.isPending;

  // A prévia mostra o que ESTÁ SENDO EDITADO na aba aberta, não o que está
  // salvo — é o ponto de ter prévia. Fora da aba `digitada`, o que está salvo
  // já é o que a prévia deve mostrar.
  const emPrevia =
    aba === 'digitada'
      ? { tipo: MeAssinaturaTipo.DIGITADA, texto, fonte, url: null, validoAte: null }
      : assinatura;

  return (
    <Card className="bg-surface-raised border-2 border-brand/20">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle className="text-sm font-semibold text-brand uppercase tracking-wide">
          Assinatura dos documentos
        </CardTitle>

        {assinatura.tipo !== MeAssinaturaTipo.NENHUMA && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2 text-destructive hover:text-destructive"
            disabled={ocupado}
            onClick={() =>
              remover.mutate(undefined, aoResponder(queryClient, 'Assinatura removida.'))
            }
          >
            <Trash2 className="w-4 h-4" />
            Remover
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        <Tabs value={aba} onValueChange={(v) => setAba(v as Aba)}>
          <TabsList className="grid grid-cols-2 sm:grid-cols-4 h-auto">
            <TabsTrigger value="digitada" className="gap-2">
              <FileSignature className="w-4 h-4" />
              Digitar
            </TabsTrigger>
            <TabsTrigger value="rubrica" className="gap-2">
              <PenTool className="w-4 h-4" />
              Desenhar
            </TabsTrigger>
            <TabsTrigger value="imagem" className="gap-2">
              <ImagePlus className="w-4 h-4" />
              Imagem
            </TabsTrigger>
            <TabsTrigger value="certificado" className="gap-2">
              <ShieldCheck className="w-4 h-4" />
              Certificado
            </TabsTrigger>
          </TabsList>

          <TabsContent value="digitada" className="space-y-4 pt-4">
            <Campo id="assinatura-texto" rotulo="Nome na assinatura">
              <Input
                id="assinatura-texto"
                className={CLASSE_CONTROLE}
                value={texto}
                maxLength={200}
                onChange={(e) => setTexto(e.target.value)}
              />
            </Campo>

            <div className="space-y-2">
              <p className="text-sm font-medium text-brand">Estilo</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {FONTES.map((nome) => (
                  <button
                    key={nome}
                    type="button"
                    // Sem o `aria-label`, o nome acessível vira a amostra
                    // inteira ("Dra. Ana Souza Caveat") — o leitor de tela lê
                    // o nome do médico cinco vezes seguidas, uma por estilo.
                    aria-label={`Estilo ${nome}`}
                    aria-pressed={fonte === nome}
                    onClick={() => setFonte(nome)}
                    className={cn(
                      'p-3 rounded-lg border-2 text-left transition-all',
                      fonte === nome ? 'border-brand bg-brand/5' : 'border-border hover:border-brand/40',
                    )}
                  >
                    <span
                      style={{ fontFamily: `'${nome}', cursive` }}
                      className="text-2xl text-foreground block truncate"
                    >
                      {texto.trim() || 'Sua assinatura'}
                    </span>
                    <span className="text-xs text-muted-foreground mt-1 block">{nome}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                className="gap-2"
                disabled={ocupado || !texto.trim()}
                onClick={() =>
                  definir.mutate(
                    { data: { tipo: MeAssinaturaTipo.DIGITADA, texto: texto.trim(), fonte } },
                    aoResponder(queryClient, 'Assinatura salva.'),
                  )
                }
              >
                {ocupado ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salvar assinatura
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="rubrica" className="pt-4">
            <AssinaturaDesenhada
              enviando={ocupado}
              aoSalvar={(arquivo) =>
                enviarImagem.mutate(
                  { data: { arquivo, tipo: MeAssinaturaTipo.DESENHADA } },
                  aoResponder(queryClient, 'Rubrica salva.'),
                )
              }
            />
          </TabsContent>

          <TabsContent value="imagem" className="pt-4">
            <CampoDeImagem
              rotulo="Assinatura digitalizada"
              ajuda="Foto ou digitalização da sua assinatura. PNG ou JPG, ao menos 200px de largura, até 1 MB. Fundo claro funciona melhor."
              urlAtual={assinatura.tipo === MeAssinaturaTipo.IMAGEM ? assinatura.url : null}
              proporcao="aspect-[3/1]"
              enviando={ocupado}
              aoEnviar={(arquivo) =>
                enviarImagem.mutate(
                  { data: { arquivo, tipo: MeAssinaturaTipo.IMAGEM } },
                  aoResponder(queryClient, 'Assinatura salva.'),
                )
              }
            />
          </TabsContent>

          <TabsContent value="certificado" className="pt-4">
            <CertificadoDigital me={me} />
          </TabsContent>
        </Tabs>

        <TabsSemQuebra>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={ocupado || assinatura.tipo === MeAssinaturaTipo.NENHUMA}
            onClick={() =>
              definir.mutate(
                { data: { tipo: MeAssinaturaTipo.NENHUMA } },
                aoResponder(queryClient, 'Os documentos voltam a sair com linha em branco.'),
              )
            }
          >
            <Minus className="w-4 h-4" />
            Deixar em branco para assinar à caneta
          </Button>
        </TabsSemQuebra>

        <PreviaDaAssinatura assinatura={emPrevia} me={me} />
      </CardContent>
    </Card>
  );
}

type Aba = 'digitada' | 'rubrica' | 'imagem' | 'certificado';

function fonteConhecida(fonte: string | null): Fonte {
  return FONTES.includes(fonte as Fonte)
    ? (fonte as Fonte)
    : DefinirAssinaturaDtoFonte.Great_Vibes;
}

/** Abre a aba do que já está salvo. `NENHUMA` cai na digitada, a mais usada. */
function abaInicial(tipo: string): Aba {
  switch (tipo) {
    case MeAssinaturaTipo.DESENHADA:
      return 'rubrica';
    case MeAssinaturaTipo.IMAGEM:
      return 'imagem';
    case MeAssinaturaTipo.CERTIFICADO:
      return 'certificado';
    default:
      return 'digitada';
  }
}

/** Wrapper só para o "deixar em branco" não ficar dentro de nenhuma aba. */
function TabsSemQuebra({ children }: { children: ReactNode }) {
  return <div className="flex justify-center border-t border-border pt-4">{children}</div>;
}
